import { useCallback, useMemo, useState } from "react";
import cn from "classnames";
import { PokemonType } from "prisma-layer";
import { ITEM_CARD_DEFINITIONS } from "game/data/item-cards/item-card-definitions";
import { getItemCardDefinition } from "utils/item-cards/get-item-card-definition";
import {
  DECK_CARD_SLOT_COUNT,
  DECK_POKE_SLOT_COUNT,
  isDeckValidForMatch,
} from "utils/item-decks/deck-validation";
import { computeEnergyFill } from "utils/item-decks/compute-energy-fill";
import type { DeckCardSlot } from "types/item-cards/deck-item-slot";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { Button } from "@/components/ui/buttons/button";
import { Dialog } from "@/components/ui/dialog/dialog";
import { Alert } from "@/components/alert/alert";
import { RouteNames } from "@/types/route-names";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import {
  useDeleteItemDeck,
  useUpdateItemDeck,
} from "@/hooks/queries/use-item-decks";
import { useItemCardInventory } from "@/hooks/queries/use-item-card-inventory";
import { useEnergyInventory } from "@/hooks/queries/use-energy-inventory";
import { useInventoryPokemons } from "@/hooks/queries/use-inventory-pokemons";
import { MiniCard, TALL_CARD_ASPECT } from "@/components/cards/card-primitives";
import { PokemonCard } from "@/components/cards/pokemon-card";
import { ItemCard } from "@/components/cards/item-card";
import { EnergyCard } from "@/components/cards/energy-card";
import type { PokemonType as EnergyCardType } from "types/battle-engine/battle";
import { getItemCardIconUrl } from "@/components/cards/utils/get-item-card-icon-url";
import { PencilIcon, TrashIcon } from "@/icons";
import type { InventoryPokemonsOutput } from "types/web-app";

type OwnedPokemon = InventoryPokemonsOutput["player_pokemons"][number];

/** Grid card width — 3 across on phones. */
const GRID_CARD_WIDTH = 96;

const slotItemKey = (slot: DeckCardSlot) =>
  slot.kind === "item"
    ? `item:${slot.item_card_key}`
    : `energy:${slot.energy_type}`;

/**
 * Item Deck Edit View — 8 poke slots + a unified 32-slot pool of item/energy
 * cards (core: unified deck slots). Energy cards occupy slots but are never
 * drawn into the live battle hand.
 */
export const DeckEditView = () => {
  const { setCurrentRoute, selectedItemDeck, previousRoute } = useSPAContext();
  const updateItemDeck = useUpdateItemDeck();
  const deleteItemDeck = useDeleteItemDeck();
  const { data: ownedItemCards } = useItemCardInventory();
  const { data: ownedEnergy } = useEnergyInventory();
  const { data: ownedPokemons } = useInventoryPokemons();

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute ?? RouteNames.ITEM_DECK_LIST);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const [deckName, setDeckName] = useState(selectedItemDeck?.name ?? "");
  const [cardSlots, setCardSlots] = useState<DeckCardSlot[]>(
    selectedItemDeck?.card_slots ?? [],
  );
  const [pokeSlots, setPokeSlots] = useState(
    selectedItemDeck?.poke_slots ?? [],
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isPokePickerOpen, setIsPokePickerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isValid = useMemo(
    () => isDeckValidForMatch(cardSlots, pokeSlots),
    [cardSlots, pokeSlots],
  );

  const missingRequirements = useMemo(() => {
    const missing: string[] = [];
    const missingPokes = DECK_POKE_SLOT_COUNT - pokeSlots.length;
    if (missingPokes > 0) missing.push(`${missingPokes} more pokemon`);
    const missingCards = DECK_CARD_SLOT_COUNT - cardSlots.length;
    if (missingCards > 0) missing.push(`${missingCards} more cards`);
    return missing;
  }, [pokeSlots.length, cardSlots.length]);

  const pokemonById = useMemo(() => {
    const map = new Map<number, OwnedPokemon>();
    for (const poke of ownedPokemons ?? []) map.set(poke.id, poke);
    return map;
  }, [ownedPokemons]);

  const placedCountByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const slot of cardSlots) {
      const key = slotItemKey(slot);
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [cardSlots]);

  const handleAddItem = useCallback(
    (itemCardKey: string) => {
      if (cardSlots.length >= DECK_CARD_SLOT_COUNT) {
        Alert(`Deck can have at most ${DECK_CARD_SLOT_COUNT} cards`);
        return;
      }
      setCardSlots((prev) => [
        ...prev,
        { kind: "item", item_card_key: itemCardKey },
      ]);
      setIsPickerOpen(false);
    },
    [cardSlots.length],
  );

  const handleAddEnergy = useCallback(
    (energyType: PokemonType) => {
      if (cardSlots.length >= DECK_CARD_SLOT_COUNT) {
        Alert(`Deck can have at most ${DECK_CARD_SLOT_COUNT} cards`);
        return;
      }
      setCardSlots((prev) => [
        ...prev,
        { kind: "energy", energy_type: energyType },
      ]);
      setIsPickerOpen(false);
    },
    [cardSlots.length],
  );

  const handleRemoveSlot = useCallback((index: number) => {
    setCardSlots((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleFillEnergy = useCallback(() => {
    const { slotsToAdd, missingByOwned, droppedForSpace } = computeEnergyFill(
      cardSlots,
      ownedEnergy ?? [],
    );

    if (slotsToAdd.length > 0) {
      setCardSlots((prev) => [...prev, ...slotsToAdd]);
    }

    const missingEntries = Object.entries(missingByOwned);
    const problems: string[] = [];
    if (missingEntries.length > 0) {
      const detail = missingEntries
        .map(([type, n]) => `${n} ${type.toLowerCase()}`)
        .join(", ");
      problems.push(`not enough owned energy (${detail})`);
    }
    if (droppedForSpace > 0) {
      problems.push(`${droppedForSpace} didn't fit in remaining slots`);
    }

    if (problems.length > 0) {
      Alert(`Filled what I could — ${problems.join("; ")}.`);
    } else if (slotsToAdd.length === 0) {
      Alert("Energy is already fully covered for the placed items.");
    }
  }, [cardSlots, ownedEnergy]);

  const handleAddPoke = useCallback(
    (pokemonId: number) => {
      if (pokeSlots.length >= DECK_POKE_SLOT_COUNT) {
        Alert(`Deck can have at most ${DECK_POKE_SLOT_COUNT} pokemon`);
        return;
      }
      setPokeSlots((prev) => [...prev, pokemonId]);
      setIsPokePickerOpen(false);
    },
    [pokeSlots.length],
  );

  const handleRemovePokeSlot = useCallback((index: number) => {
    setPokeSlots((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving || !selectedItemDeck) return;

    if (!deckName.trim()) {
      Alert("Deck must have a name");
      return;
    }

    setIsSaving(true);
    try {
      const response = await updateItemDeck.mutateAsync({
        deck_id: selectedItemDeck.id,
        name: deckName.trim(),
        card_slots: cardSlots,
        poke_slots: pokeSlots,
      });

      if (response.ok) {
        setCurrentRoute(RouteNames.ITEM_DECK_LIST);
      } else {
        Alert(response.message || "Failed to save deck. Please try again.");
      }
    } catch (error) {
      console.error("Failed to save item deck:", error);
      Alert("Failed to save deck. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    selectedItemDeck,
    deckName,
    cardSlots,
    pokeSlots,
    updateItemDeck,
    setCurrentRoute,
  ]);

  const handleDelete = useCallback(async () => {
    if (isDeleting || !selectedItemDeck) return;

    setIsDeleting(true);
    try {
      const response = await deleteItemDeck.mutateAsync({
        deck_id: selectedItemDeck.id,
      });
      if (response.ok) {
        setCurrentRoute(RouteNames.ITEM_DECK_LIST);
      } else {
        Alert(response.message || "Failed to delete deck. Please try again.");
      }
    } catch (error) {
      console.error("Failed to delete item deck:", error);
      Alert("Failed to delete deck. Please try again.");
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  }, [isDeleting, selectedItemDeck, deleteItemDeck, setCurrentRoute]);

  if (!selectedItemDeck) {
    Alert("Unexpected error: No deck selected");
    setCurrentRoute(RouteNames.ITEM_DECK_LIST);
    return null;
  }

  return (
    <div className="relative flex flex-col bg-black/50 backdrop-blur-[5px] w-full h-full overflow-hidden">
      <div className="relative flex flex-col gap-3 px-4 pt-4 pb-24 w-full h-full overflow-y-auto">
        {/* Top row: deck name (editable) + delete */}
        <div className="z-10 flex items-center gap-2 mx-auto w-full max-w-md">
          <div className="flex flex-1 items-center gap-2 bg-pk-blue-3/60 px-3 py-2 border-2 border-pk-blue-4 rounded-lg min-w-0">
            <input
              type="text"
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              placeholder="Deck name..."
              maxLength={20}
              className="flex-1 bg-transparent outline-none min-w-0 font-bold text-white"
            />
            <PencilIcon
              width={16}
              height={16}
              className="flex-shrink-0 text-pk-blue-6"
            />
          </div>
          <button
            type="button"
            onClick={() => setIsDeleteDialogOpen(true)}
            aria-label="Delete deck"
            className="flex flex-shrink-0 justify-center items-center bg-pk-red-3/70 hover:bg-pk-red-3 border-2 border-pk-red-4 rounded-lg w-10 h-10 text-pk-red-5 transition-colors"
          >
            <TrashIcon width={18} height={18} />
          </button>
        </div>

        <div className="flex flex-col gap-3 mx-auto w-full max-w-md">
          {/* Pokemon slots */}
          <SlotSection
            title={`POKEMON (${pokeSlots.length}/${DECK_POKE_SLOT_COUNT})`}
          >
            {Array.from({ length: DECK_POKE_SLOT_COUNT }, (_, index) => {
              const pokemonId = pokeSlots[index];
              const pokemon =
                pokemonId !== undefined
                  ? pokemonById.get(pokemonId)
                  : undefined;

              return (
                <SlotButton
                  key={index}
                  filled={pokemon !== undefined}
                  onClick={() =>
                    pokemonId !== undefined
                      ? handleRemovePokeSlot(index)
                      : setIsPokePickerOpen(true)
                  }
                >
                  {pokemon && (
                    <MiniCard width={GRID_CARD_WIDTH} aspect={TALL_CARD_ASPECT}>
                      <PokemonCard
                        pokeName={pokemon.base_pokemon_name}
                        level={pokemon.level}
                        activeSkills={pokemon.active_skills}
                        isShiny={pokemon.is_shiny}
                        isFullArt={pokemon.is_full_art}
                      />
                    </MiniCard>
                  )}
                </SlotButton>
              );
            })}
          </SlotSection>

          {/* Card slots (items + energy) */}
          <SlotSection
            title={`CARDS (${cardSlots.length}/${DECK_CARD_SLOT_COUNT})`}
            action={
              <button
                type="button"
                onClick={handleFillEnergy}
                className="flex items-center gap-1 bg-pk-yellow-1 hover:bg-pk-yellow-2 shadow-cartoon-sm px-2 py-1 rounded-sm font-bold text-white text-[10px] uppercase tracking-wide transition-colors"
              >
                Fill Energy
              </button>
            }
          >
            {Array.from({ length: DECK_CARD_SLOT_COUNT }, (_, index) => {
              const slot = cardSlots[index];

              return (
                <SlotButton
                  key={index}
                  filled={slot !== undefined}
                  onClick={() =>
                    slot ? handleRemoveSlot(index) : setIsPickerOpen(true)
                  }
                >
                  {slot && (
                    <MiniCard width={GRID_CARD_WIDTH} aspect={TALL_CARD_ASPECT}>
                      <SlotCard slot={slot} />
                    </MiniCard>
                  )}
                </SlotButton>
              );
            })}
          </SlotSection>
        </div>
      </div>

      {/* Floating save + requirement warning — overlays content, stays on scroll */}
      <div className="right-4 bottom-4 z-20 absolute flex flex-col items-end gap-1.5 pointer-events-none">
        {!isValid && (
          <span className="bg-pk-red-3/95 shadow-cartoon-sm px-3 py-1 border-2 border-pk-red-4 rounded-lg font-bold text-white text-xs">
            Needs {missingRequirements.join(", ")}
          </span>
        )}
        <div className="pointer-events-auto">
          <Button
            variant="blue"
            onClick={handleSave}
            disabled={isSaving}
            className="px-8 py-3"
          >
            <span className="font-bold text-base">
              {isSaving ? "Saving..." : "Save"}
            </span>
          </Button>
        </div>
      </div>

      <CardPickerDialog
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        ownedItemCards={ownedItemCards ?? []}
        ownedEnergy={ownedEnergy ?? []}
        placedCountByKey={placedCountByKey}
        onPickItem={handleAddItem}
        onPickEnergy={handleAddEnergy}
      />

      <PokePickerDialog
        isOpen={isPokePickerOpen}
        onClose={() => setIsPokePickerOpen(false)}
        ownedPokemons={ownedPokemons ?? []}
        placedPokeIds={pokeSlots}
        onPick={handleAddPoke}
      />

      <Dialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        closeOnBackdrop={!isDeleting}
      >
        <StandardFrame variant="red" className="w-[85vw] max-w-md mx-auto">
          <div className="text-center">
            <p className="mb-4">
              Are you sure you want to delete "{deckName}"?
            </p>
            <div className="flex gap-4 items-center justify-center w-full">
              <Button
                variant="gray"
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeleting}
                className="flex-1"
              >
                CANCEL
              </Button>
              <Button
                variant="red"
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1"
              >
                {isDeleting ? "DELETING..." : "DELETE DECK"}
              </Button>
            </div>
          </div>
        </StandardFrame>
      </Dialog>
    </div>
  );
};

/** Card art for a filled deck slot (item or energy). */
const SlotCard = ({ slot }: { slot: DeckCardSlot }) => {
  if (slot.kind === "energy")
    return <EnergyCard type={slot.energy_type as unknown as EnergyCardType} />;
  const def = getItemCardDefinition(slot.item_card_key);
  if (!def) return null;
  return <ItemCard def={def} iconUrl={getItemCardIconUrl(def)} />;
};

const SlotSection = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="relative shadow-cartoon border-4 border-gray-900 rounded-xl overflow-hidden">
    <div className="relative bg-gradient-to-br from-pk-blue-2 to-pk-blue-1 p-3">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: "url('/assets/app-ui/textre.png')",
          backgroundSize: "cover",
          backgroundRepeat: "repeat",
        }}
      />
      <div className="z-10 relative flex justify-between items-center mb-2">
        <StrokedText strokeWidth={8} fontSize={14}>
          {title}
        </StrokedText>
        {action}
      </div>
      <div className="z-10 relative gap-2 grid grid-cols-4 justify-items-center">
        {children}
      </div>
    </div>
  </div>
);

const SlotButton = ({
  filled,
  onClick,
  children,
}: {
  filled: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "relative flex justify-center items-center rounded-lg transition-all active:scale-95",
      !filled &&
        "border-2 border-pk-gray-2 border-dashed bg-pk-gray-1/20 w-[96px] h-[146px]",
    )}
  >
    {filled ? (
      <>
        <span className="top-1 right-1 z-20 absolute flex justify-center items-center bg-pk-red-1 rounded-full w-4 h-4 text-[10px] text-white leading-none">
          ×
        </span>
        {children}
      </>
    ) : (
      <span className="opacity-30 text-pk-gray-4 text-2xl">+</span>
    )}
  </button>
);

type OwnedItemCard = { item_card_key: string; amount: number };
type OwnedEnergy = { type: PokemonType; amount: number };

const CardPickerDialog = ({
  isOpen,
  onClose,
  ownedItemCards,
  ownedEnergy,
  placedCountByKey,
  onPickItem,
  onPickEnergy,
}: {
  isOpen: boolean;
  onClose: () => void;
  ownedItemCards: OwnedItemCard[];
  ownedEnergy: OwnedEnergy[];
  placedCountByKey: Map<string, number>;
  onPickItem: (itemCardKey: string) => void;
  onPickEnergy: (energyType: PokemonType) => void;
}) => {
  const [tab, setTab] = useState<"items" | "energy">("items");
  const [search, setSearch] = useState("");

  const ownedItemCountByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const card of ownedItemCards) map.set(card.item_card_key, card.amount);
    return map;
  }, [ownedItemCards]);

  const pickableItems = useMemo(
    () =>
      ITEM_CARD_DEFINITIONS.filter((def) => {
        const owned = ownedItemCountByKey.get(def.key) ?? 0;
        const placed = placedCountByKey.get(`item:${def.key}`) ?? 0;
        if (owned === 0 || placed >= owned) return false;
        return def.name.toLowerCase().includes(search.trim().toLowerCase());
      }),
    [ownedItemCountByKey, placedCountByKey, search],
  );

  const pickableEnergy = useMemo(
    () => ownedEnergy.filter((energy) => energy.amount > 0),
    [ownedEnergy],
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <StandardFrame
        variant="blue"
        className="w-[92vw] max-w-lg mx-auto max-h-[78vh]"
      >
        <div className="flex flex-col p-3">
        <StrokedText
          strokeWidth={10}
          fontSize={18}
          className="mb-3 text-center"
        >
          ADD A CARD
        </StrokedText>

        <div className="flex gap-2 mb-3">
          {(["items", "energy"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "flex-1 py-1.5 rounded font-bold text-xs uppercase tracking-wide transition-colors",
                tab === t
                  ? "bg-pk-blue-3 text-white"
                  : "bg-pk-gray-1/40 text-pk-gray-4 hover:text-white",
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "items" && (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cards..."
            className="mb-3 w-full bg-pk-gray-1/50 rounded px-3 py-2 text-sm text-pk-white placeholder-pk-gray-4 outline-none border border-pk-gray-4"
          />
        )}

        <div className="grid grid-cols-2 xs:grid-cols-3 gap-3 justify-items-center overflow-y-auto content-start">
          {tab === "items"
            ? pickableItems.map((def) => {
                const owned = ownedItemCountByKey.get(def.key) ?? 0;
                const placed = placedCountByKey.get(`item:${def.key}`) ?? 0;
                return (
                  <PickerCard
                    key={def.key}
                    badge={`${placed}/${owned}`}
                    onClick={() => onPickItem(def.key)}
                  >
                    <ItemCard def={def} iconUrl={getItemCardIconUrl(def)} />
                  </PickerCard>
                );
              })
            : pickableEnergy.map((energy) => {
                const placed =
                  placedCountByKey.get(`energy:${energy.type}`) ?? 0;
                return (
                  <PickerCard
                    key={energy.type}
                    badge={`${placed}/${energy.amount}`}
                    disabled={placed >= energy.amount}
                    onClick={() => onPickEnergy(energy.type)}
                  >
                    <EnergyCard
                      type={energy.type as unknown as EnergyCardType}
                    />
                  </PickerCard>
                );
              })}
        </div>

        <Button variant="gray" onClick={onClose} className="mt-3">
          Close
        </Button>
        </div>
      </StandardFrame>
    </Dialog>
  );
};

const PickerCard = ({
  badge,
  disabled = false,
  onClick,
  children,
}: {
  badge: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "relative transition-transform active:scale-95",
      disabled && "opacity-40 pointer-events-none",
    )}
  >
    <span className="top-1 right-1 z-20 absolute bg-pk-night-1/70 px-1 rounded-sm text-[9px] text-pk-gray-5">
      {badge}
    </span>
    <MiniCard width={GRID_CARD_WIDTH} aspect={TALL_CARD_ASPECT}>
      {children}
    </MiniCard>
  </button>
);

const PokePickerDialog = ({
  isOpen,
  onClose,
  ownedPokemons,
  placedPokeIds,
  onPick,
}: {
  isOpen: boolean;
  onClose: () => void;
  ownedPokemons: OwnedPokemon[];
  placedPokeIds: number[];
  onPick: (pokemonId: number) => void;
}) => {
  const [search, setSearch] = useState("");
  const placedSet = useMemo(() => new Set(placedPokeIds), [placedPokeIds]);
  const pickablePokemons = useMemo(() => {
    const query = search.trim().toLowerCase();
    return ownedPokemons
      .filter((poke) => {
        if (placedSet.has(poke.id)) return false;
        if (!query) return true;
        return (poke.name || poke.base_pokemon_name)
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => b.level - a.level);
  }, [ownedPokemons, placedSet, search]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <StandardFrame
        variant="blue"
        className="!w-[95vw] !m-0 max-w-lg mx-auto max-h-[75vh]"
      >
        <div className="flex flex-col p-3">
        <StrokedText
          strokeWidth={10}
          fontSize={18}
          className="mb-3 text-center"
        >
          PICK A POKEMON
        </StrokedText>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search pokemon..."
          className="mb-3 w-full bg-pk-gray-1/50 rounded px-3 py-2 text-sm text-pk-white placeholder-pk-gray-4 outline-none border border-pk-gray-4"
        />
        <div className="grid grid-cols-3 xs:grid-cols-4 gap-0.5 justify-items-center overflow-y-auto content-start">
          {pickablePokemons.map((poke) => (
            <PickerCard
              key={poke.id}
              badge={`Lv${poke.level}`}
              onClick={() => onPick(poke.id)}
            >
              <PokemonCard
                pokeName={poke.base_pokemon_name}
                level={poke.level}
                activeSkills={poke.active_skills}
                isShiny={poke.is_shiny}
                isFullArt={poke.is_full_art}
              />
            </PickerCard>
          ))}
        </div>
        <Button variant="gray" onClick={onClose} className="mt-3">
          Close
        </Button>
        </div>
      </StandardFrame>
    </Dialog>
  );
};
